// pages/tests/MonthlyTestForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AuthService from '../../Auth/AuthService';
import { SECTIONS, ACADEMIC_YEARS } from '../../utils/constants';
import { testsApi } from './api/apiClientMonthly';

const MonthlyTestForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    academicYear: '2024-2025',
    standard: '',
    section: '',
    subject: '',
    testName: '',
    testDate: '',
    maxMarks: '',
    passingMarks: '',
    instructions: '',
    status: 'DRAFT',
  });

  const [errors, setErrors] = useState({});
  const [classes, setClasses] = useState([]);
  // const [sections, setSections] = useState(SECTIONS);
  // const [subjects, setSubjects] = useState(SUBJECTS);

  useEffect(() => {
    fetchClasses();
    if (isEditMode) {
      fetchTestData();
    }
  }, [isEditMode]);

  const fetchClasses = async () => {
    try {
      const url = AuthService.pathUrl('GET', "/fees/structure");
      const response = await AuthService.getAuth(url);
      setClasses(response);
    } catch (error) {
      console.error("Error fetching fees structures:", error);
    }
  };

  const fetchTestData = async () => {
    setLoading(true);
    try {
      const response = await testsApi.getById(id);
      const data = response?.data ?? {};

      setFormData({
        ...data,
        testDate: data.testDate ? new Date(data.testDate) : null,
      });
    } catch (error) {
      setError('Failed to load test data');
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.academicYear) newErrors.academicYear = 'Academic year is required';
      if (!formData.standard) newErrors.standard = 'Standard is required';
      if (!formData.section) newErrors.section = 'Section is required';
      if (!formData.subject) newErrors.subject = 'Subject is required';
      if (!formData.testName) newErrors.testName = 'Test name is required';

      if (!formData.testDate) newErrors.testDate = 'Test date is required';
      if (!formData.maxMarks) newErrors.maxMarks = 'Maximum marks is required';
      if (!formData.passingMarks) newErrors.passingMarks = 'Passing marks is required';
      if (formData.passingMarks && formData.maxMarks &&
        parseInt(formData.passingMarks) > parseInt(formData.maxMarks)) {
        newErrors.passingMarks = 'Passing marks cannot exceed maximum marks';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [field]: value
    });
    if (field === "standard") {
      const selectedData = classes.find((f) => f.standard === value);
      setFormData((prev) => ({
        ...prev,
        standard: value,
        section: selectedData ? selectedData.section : "A",
      }))
    }
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      testDate: date
    });
    if (errors.testDate) {
      setErrors({
        ...errors,
        testDate: ''
      });
    }
  };

  const handleSubmit = async (status = null) => {
    if (!validateStep(0) || !validateStep(1)) {
      setError('Please fill all required fields');
      return;
    }
    setLoading(true);
    setError('');

    try {

      const submitData = {
        ...formData,
        status: status || formData.status
      };

      if (isEditMode) {
        await testsApi.update(id, submitData);
      } else {
        await testsApi.create(submitData);
      }

      setSuccess(`Test ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => {
        navigate('/tests');
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save test');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} size={2}>
          <FormControl fullWidth error={!!errors.academicYear}>
            <InputLabel>Academic Year *</InputLabel>
            <Select
              value={formData.academicYear}
              onChange={handleChange('academicYear')}
              label="Academic Year *"
            >
              {ACADEMIC_YEARS.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
            {errors.academicYear && (
              <FormHelperText>{errors.academicYear}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} size={2}>
          <FormControl fullWidth error={!!errors.standard}>
            <InputLabel>Standard *</InputLabel>
            <Select
              value={formData.standard}
              onChange={handleChange('standard')}
              label="standard *"
            >
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.standard}>
                  {cls.standard}
                </MenuItem>
              ))}
            </Select>
            {errors.standard && (
              <FormHelperText>{errors.standard}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} size={1.5}>
          <FormControl fullWidth error={!!errors.section} disabled={!formData.standard}>
            <InputLabel>Section *</InputLabel>
            <Select
              value={formData.section}
              onChange={handleChange('section')}
              label="Section *"
            >
              {SECTIONS.map((section) => (
                <MenuItem key={section} value={section}>
                  {section}
                </MenuItem>
              ))}
            </Select>
            {errors.section && (
              <FormHelperText>{errors.section}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Subject Name *"
            value={formData.subject}
            onChange={handleChange('subject')}
            error={!!errors.subject}
            helperText={errors.subject}
            placeholder="e.g., English, Hindi, Sanskrit"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Test Name *"
            value={formData.testName}
            onChange={handleChange('testName')}
            error={!!errors.testName}
            helperText={errors.testName}
            placeholder="e.g., Monthly Test - January 2024"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Test Date *"
              value={formData.testDate}
              onChange={handleDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.testDate}
                  helperText={errors.testDate}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Maximum Marks *"
            value={formData.maxMarks}
            onChange={handleChange('maxMarks')}
            error={!!errors.maxMarks}
            helperText={errors.maxMarks}
            slotProps={{
              htmlInput: { min: 0 }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6} size={2}>
          <TextField
            fullWidth
            type="number"
            label="Passing Marks *"
            value={formData.passingMarks}
            onChange={handleChange('passingMarks')}
            error={!!errors.passingMarks}
            helperText={errors.passingMarks}
            slotProps={{ htmlInput: { min: 0, max: formData.maxMarks || 100 } }}
          />
        </Grid>

        <Grid item xs={12} md={6} size={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={handleChange('status')}
              label="Status"
            >
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="OPEN">Open</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="PUBLISHED">Published</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6} size={5}>
          <TextField
            fullWidth
            label="Instructions"
            value={formData.instructions}
            onChange={handleChange('instructions')}
            placeholder="Enter any special instructions for students..."
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Monthly Test' : 'Create Monthly Test'}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}


      {/* Stepper */}
      <Paper sx={{ p: 3 }}>

        <Typography variant='subtitle1' sx={{ mb: 3 }}>{'Basic Test Information'}</Typography>

        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>

        {/* Navigation Buttons */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => navigate('/tests')}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => handleSubmit('DRAFT')}
              disabled={loading}
            >
              Save as Draft
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => handleSubmit()}
              disabled={loading}
            >
              {isEditMode ? 'Update' : 'Create'} Test
            </Button>

          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MonthlyTestForm;